// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Builder-only test tools from the wrist (M13-25, 11 §4).
//
// The 2-D Builder's test play has a strip of tools that no scored game may have — freeze and
// single-step, place ball, slow motion, the last three seconds of the ball's path — and 11 §4
// gives them "available only in the Builder and never in a scored game". On the bench they hang
// off the wrist: while a draft is under Test play the Build page becomes the test page, whose
// rows are these tools, and every one of them acts on the *draft's* session and nothing else —
// the host allows them only while the draft is under test (`MachineRuntime.buildTest`), which is
// the same gate the Builder's `testTools` option is, held by the same object.
//
// This file is the pure part: what the rows state, which speed comes next, whether a pinch on
// the playfield is the one that places the ball. The freeze itself is the session's pause, the
// step is the driver's, the speed is the frame clock's and the ball is the world's — all the
// host's, through `BuildHost.tools`. Nothing here knows Three or Unity; `TestTools.cs` is this
// file, case for case.
//
// **Place ball is armed from the wrist and spent by a pinch.** A row cannot say where; a hand
// can. Pressing *Place ball* closes the menu and arms this; the next pinch that closes over the
// board — in the playfield's own frame, inside its footprint, up to the glass — is where the ball
// goes, and the arming is spent whether or not the world had a ball to move (the host says which).
// A pinch in the air, or on the wrist, is not a placement.
//
// **Add ball is armed the same way** (the test-tool follow-up, 26 Sep 2026: "a second ball in a
// test"): the next pinch over the board puts one *more* ball there, at rest, beside the one in
// play — the multiball an author wants to watch without first playing their way into it. The two
// armings are one or the other: arming either disarms its twin, so one pinch never means both.

import type { Point2 } from '../../../core/ts/machine'
import { overBoard, type CarryBoard, type CarryHand } from './partCarry'
import { RIBBON_FAST, RIBBON_SLOW } from '../../engine/ballTrail'
import { benchText, type BenchStrings } from './benchStrings'

/** 11 §4's range: 1× down to a tenth, in the steps a wrist row cycles through. */
export const SPEEDS: readonly number[] = [1, 0.5, 0.25, 0.1]

/** What the test page's rows state. */
export interface TestToolsState {
  /** The simulation is stopped — the session's own pause, read from the host. */
  readonly frozen: boolean
  /** How fast time runs, one of `SPEEDS`. */
  readonly speed: number
  /** Place ball is armed: the next pinch over the board places it. */
  readonly placing: boolean
  /** Add ball is armed: the next pinch over the board puts one more ball there. */
  readonly adding: boolean
  /** The trajectory ribbon is drawn. */
  readonly trail: boolean
}

/** A held row starts repeating after this long, milliseconds — longer than a tap, so a tap is one step. */
export const REPEAT_DELAY_MS = 400
/** …and then repeats this often: ten steps a second, a millisecond of play each, slow enough to count. */
export const REPEAT_EVERY_MS = 100

/**
 * A row held down, turned into repeats (polish to M13-25): nothing for the first
 * `REPEAT_DELAY_MS` — the press itself has already acted — then one every `REPEAT_EVERY_MS`, as
 * many as the frame's time covers. Let go, and it starts again from nothing.
 */
export class HoldRepeat {
  private heldFor = 0
  private fired = 0

  advance(held: boolean, frameMs: number): number {
    if (!held) {
      this.release()
      return 0
    }
    this.heldFor += Math.max(0, frameMs)
    const due = this.heldFor < REPEAT_DELAY_MS ? 0 : 1 + Math.floor((this.heldFor - REPEAT_DELAY_MS) / REPEAT_EVERY_MS)
    const now = due - this.fired
    this.fired = due
    return now
  }

  release(): void {
    this.heldFor = 0
    this.fired = 0
  }
}

export class TestTools {
  private readonly stepHold = new HoldRepeat()
  private speedIndex = 0
  private placingNow = false
  private addingNow = false
  private addedNow = false
  private trailOn = true
  /** The hand holding the ball being placed, if one is (the 1.0.109 round). */
  private holder: CarryHand['handedness'] | undefined
  /** Where the held ball would go: the holding pinch's last point over the board. */
  private heldAt: Point2 = [0, 0]
  /** Whether each hand was pinched last frame — starting closed, deliberately (`CabinetGrab`, `PartCarry`). */
  private readonly wasGrab = { left: true, right: true }

  get speed(): number {
    return SPEEDS[this.speedIndex] ?? 1
  }

  /** The next speed round: 1 → ½ → ¼ → ⅒ → 1. Returns the new one. */
  nextSpeed(): number {
    this.speedIndex = (this.speedIndex + 1) % SPEEDS.length
    return this.speed
  }

  get placing(): boolean {
    return this.placingNow
  }

  /** Arm Place ball: the next pinch over the board is where it goes. Pressed again, disarms. */
  togglePlacing(): boolean {
    this.placingNow = !this.placingNow
    if (this.placingNow) this.addingNow = false
    this.holder = undefined
    return this.placingNow
  }

  get adding(): boolean {
    return this.addingNow
  }

  /** Arm Add ball: the next pinch over the board puts a second ball there. Pressed again, disarms. */
  toggleAdding(): boolean {
    this.addingNow = !this.addingNow
    if (this.addingNow) this.placingNow = false
    this.holder = undefined
    return this.addingNow
  }

  /**
   * Whether the point `advance` last answered is for a ball to be *added* rather than the one in
   * play moved — which arming the pinch spent.
   */
  get added(): boolean {
    return this.addedNow
  }

  get trail(): boolean {
    return this.trailOn
  }

  /** A pinch is holding the ball to be placed or added. */
  get heldBy(): CarryHand['handedness'] | undefined { return this.holder }

  /**
   * Whether the wrist menu takes this hand's pinch from the tools (1.0.249): the pointing hand's,
   * while the menu has it — never the hand already holding the ball. On the owner's 27 Oct 02:54
   * log three balls went down 121-318 ms after they were taken, as the menu caught the holding
   * right hand. `TestTools.MenuMasks` is the twin.
   */
  static menuMasks(menuEngaged: boolean, side: CarryHand['handedness'], heldBy: CarryHand['handedness'] | undefined): boolean {
    return menuEngaged && side === 'right' && heldBy !== 'right'
  }

  get holding(): boolean {
    return this.holder !== undefined
  }

  /** Where the held ball would go if the pinch opened now, or nothing. */
  get held(): Point2 | undefined {
    return this.holder !== undefined ? this.heldAt : undefined
  }

  toggleTrail(): boolean {
    this.trailOn = !this.trailOn
    return this.trailOn
  }

  /**
   * One frame of pinch points in the playfield's frame while a test runs. Answers the point the
   * ball is to be placed (or added, `added`) at, or nothing. While armed, a closing edge over the
   * board takes the ball into that hand; it follows the pinch over the board, and when the pinch
   * opens over the board the ball goes to the last point it was held at and the arming is spent
   * (the 1.0.109 round, 27 Sep 2026: "placing a ball gets dropped on board before player releases
   * ball"). Opened off the board, or the hand lost, and the hold ends with the tool still armed.
   */
  advance(hands: readonly CarryHand[], board: CarryBoard): Point2 | undefined {
    let placed: Point2 | undefined
    for (const hand of hands) {
      const grab = hand.present && hand.grab
      const edge = grab && !this.wasGrab[hand.handedness]
      this.wasGrab[hand.handedness] = hand.present ? hand.grab : true
      const over = hand.present && overBoard(hand.x, hand.y, hand.z, board)
      if (this.holder === hand.handedness) {
        if (grab) {
          if (over) this.heldAt = [hand.x, hand.y]
          continue
        }
        this.holder = undefined
        if (!over || placed !== undefined) continue
        placed = this.heldAt
        this.addedNow = this.addingNow
        this.placingNow = false
        this.addingNow = false
        continue
      }
      if (!edge || !(this.placingNow || this.addingNow) || this.holder !== undefined || placed !== undefined) continue
      if (!over) continue
      this.holder = hand.handedness
      this.heldAt = [hand.x, hand.y]
    }
    return placed
  }

  /**
   * The Step row held down while frozen (polish): how many more steps this frame, after the
   * press's own. `held` is the host's answer — the finger still on the Step row and the session
   * still frozen.
   */
  stepRepeats(held: boolean, frameMs: number): number {
    return this.stepHold.advance(held, frameMs)
  }

  /** The test is over, or never began: everything back to its first state. */
  reset(): void {
    this.stepHold.release()
    this.speedIndex = 0
    this.placingNow = false
    this.addingNow = false
    this.addedNow = false
    this.holder = undefined
    this.trailOn = true
    this.wasGrab.left = true
    this.wasGrab.right = true
  }

  /** What the rows state, given the host's answer about the pause. */
  state(frozen: boolean): TestToolsState {
    return { frozen, speed: this.speed, placing: this.placingNow, adding: this.addingNow, trail: this.trailOn }
  }
}

/** A speed as the row prints it: `1`, `0.5`, `0.25`, `0.1`. */
/**
 * Whether a game under test has left play for good — over, asking for initials, or back in attract
 * (the 1.0.108 round, 27 Sep 2026: "Add ball shows the circle but no ball appears"). The three test
 * balls had drained, the game was asking for initials, and Add ball was refused with nothing said
 * about why. A test is for playing the layout, so its game is served again as soon as it ends.
 */
export function wantsANewGame(phase: string): boolean {
  return phase === 'gameOver' || phase === 'highScoreEntry' || phase === 'attract'
}

export function speedText(speed: number): string {
  return Number(speed.toFixed(2)).toString()
}

/** One row of the ribbon's key: which band (0 slow, 1 medium, 2 fast) and what it says. */
export interface RibbonLegendRow {
  readonly band: 0 | 1 | 2
  readonly text: string
}

/**
 * The key to the ribbon's colours (the test-tool follow-up, 25 Sep 2026): the three speed bands in
 * the player's language, with the thresholds the bands are drawn at — `RIBBON_SLOW` and
 * `RIBBON_FAST`, so the key cannot say one number while the ribbon draws another.
 * `TestTools.RibbonLegend` is the twin.
 */
export function ribbonLegend(strings: BenchStrings): readonly RibbonLegendRow[] {
  const values = { slow: String(RIBBON_SLOW), fast: String(RIBBON_FAST) }
  return [
    { band: 0, text: benchText(strings, 'ribbon.slow', values) },
    { band: 1, text: benchText(strings, 'ribbon.medium', values) },
    { band: 2, text: benchText(strings, 'ribbon.fast', values) },
  ]
}
